/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useTokenListByChain } from "hooks/useTokenListByChain";
import React, { useEffect, useMemo } from "react";
import { useBalanceForSymbol } from "../../hooks/useBalanceForSymbol";
import "../../pages/Trade/index.css";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Token } from "../../store/global/TokenEnum";
import { Amount } from "../../types/amount";
import { getPriceForTokenByTokenList } from "../../utils/tokenListUtils";
import { BalanceDetails } from "./sections/BalanceDetails";
import { TokenInputSection } from "./sections/TokenInputSection";
import { useDisplayOnClick } from "hooks/useDisplayOnClick";
import { AnimateChangeInHeight } from "components/general/AnimateChangeInHeight";
import { canLock, handleBalanceOption } from "utils/tradeUtils";
import { Text } from "components/general/Text";
import { TradeLegMakerSlider } from "./sections/TradeLegMakerSlider";
import { AmountAndUsdPriceFullWidth } from "./sections/elements/AmountAndUsdPriceFullWidth";
import { isWrapTrade } from "utils/orderTypeUtils";
import { LockButton } from "./sections/LockButton";
import { BalancePercentageOptionsEnum } from "./sections/BalancePercentages";
import { useGetMaxAmountToWrap } from "../../hooks/wrap/useGetMaxAmountToWrap";
import { AmountLabel, getLabelType } from "./sections/AmountLabel";
import { InteractionStore } from "store/interactions/InteractionStore";

export type CounterParty = "maker" | "taker";

export interface TradeLegMakerProps {
  symbol: Token;
  showPlusButton: boolean;
  setIsWaitingToAddToken: React.Dispatch<React.SetStateAction<boolean>>;
  isWaitingToAddToken: boolean;
  setIsChangingToken: React.Dispatch<React.SetStateAction<Token | undefined>>;
}

export const TradeLegMaker: React.FC<TradeLegMakerProps> = ({
  symbol,
  showPlusButton,
  setIsWaitingToAddToken,
  setIsChangingToken,
  isWaitingToAddToken,
}) => {
  const tokenList = useTokenListByChain();
  const {
    address,
    quote,
    network,
    isSending,
    isSliderMoving,
    isRequestingQuote,
    makerTokens,
    takerTokens,
    theme,
  } = GlobalStore.useState(s => ({
    address: s.address,
    quote: s.quote,
    network: s.network,
    isSending: s.isSending,
    isSliderMoving: s.isSliderMoving,
    isRequestingQuote: s.isRequestingQuote,
    makerTokens: s.makerTokens,
    takerTokens: s.takerTokens,
    theme: s.theme,
  }));
  const balance = useBalanceForSymbol(symbol);
  const getMaxAmountToWrap = useGetMaxAmountToWrap();
  const selectedToken = makerTokens.find(entry => entry.token === symbol);
  const tokenListPrice = getPriceForTokenByTokenList(symbol, tokenList);
  const quotePrice = quote?.makerTokens[symbol]?.priceUsd;
  const price = quotePrice ?? tokenListPrice;
  const tradeLegToOpenOnAmountBarClick = InteractionStore.useState(
    s => s.tradeLegToOpenOnAmountBarClick
  );

  const {
    isDisplayed: isInputFocused,
    toggleDisplay: onTradeLegClick,
    elementToClickRef: tradeLegRef,
    elementToDisplayRef: sliderRef,
    forceOpen: openBalancePercentages,
    forceClose: hideBalancePercentages,
  } = useDisplayOnClick();

  useEffect(() => {
    if (tradeLegToOpenOnAmountBarClick === symbol) {
      openBalancePercentages();
      InteractionStore.update(s => (s.tradeLegToOpenOnAmountBarClick = undefined));
    }
  }, [openBalancePercentages, symbol, tradeLegToOpenOnAmountBarClick]);

  const pct =
    GlobalStore.useState(s => s.makerTokens.find(entry => entry.token === symbol)?.pct) ?? 1;
  const is12M = makerTokens.length > 1;
  const amount = useMemo(() => {
    if (isWrapTrade(takerTokens, makerTokens, network)) return takerTokens[0].amount;
    return (
      (selectedToken?.token != null
        ? quote?.makerTokens[selectedToken.token]?.amountBeforeFee ?? Amount.ZERO
        : Amount.ZERO) ?? Amount.ZERO
    );
  }, [makerTokens, network, quote?.makerTokens, selectedToken?.token, takerTokens]);

  if (tokenList == null || selectedToken == null) return null;

  const color = tokenList?.[symbol]?.colour ?? theme.font.primary.default;
  const decimals = tokenList?.[symbol]?.chainInfo.decimals ?? 18;

  return (
    <Container>
      <TradeRowWrapper
        ref={tradeLegRef as React.MutableRefObject<HTMLDivElement>}
        data-testid={"trade-leg"}
        showSlider={isInputFocused && is12M}
        counterParty={"maker"}
        heightAddOn={24}
        onClick={() => {
          GlobalStore.update(s => {
            s.orderError = undefined;
          });
          if (isInputFocused) return;
          onTradeLegClick();
        }}
      >
        <TokenInputAndBalanceContainer>
          <MainSection>
            <TokenInputSection
              symbol={symbol}
              showPlusButton={showPlusButton}
              counterParty={"maker"}
              isWaitingToAddToken={isWaitingToAddToken}
              setIsWaitingToAddToken={setIsWaitingToAddToken}
              setIsChangingToken={setIsChangingToken}
              hideBalancePercentages={hideBalancePercentages}
            />
            <AmountLabel
              amountToDisplay={amount.value.gt(0) ? amount.toDisplay(symbol, tokenList) : ""}
              labelType={getLabelType(amount)}
              isVisible={!is12M}
            />
            {is12M && (
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {makerTokens.length > 2 && selectedToken.isLocked && !isInputFocused && (
                  <LockButton
                    symbol={symbol}
                    canLock={canLock("maker", makerTokens)}
                    isLocked={selectedToken?.isLocked}
                    size={20}
                    iconSize={16}
                  />
                )}
                {!isInputFocused && <PercentageDisplay pct={pct} color={color} />}
              </div>
            )}
          </MainSection>
          <SubSection>
            {address && balance != null && (
              <BalanceDetails
                symbol={symbol}
                balance={balance}
                price={price ? price : Amount.ZERO}
                amount={amount}
                onBalanceClick={() => {
                  void handleBalanceOption(
                    symbol,
                    "maker",
                    BalancePercentageOptionsEnum.Max,
                    network,
                    balance,
                    getMaxAmountToWrap,
                    decimals
                  );
                }}
                isBalanceShown={!isSending}
                isAmountShown={is12M && !isInputFocused}
                isUsdValueShown={!isInputFocused}
              />
            )}
          </SubSection>
        </TokenInputAndBalanceContainer>
        {isInputFocused && balance != null && is12M && (
          <>
            <SliderContainer>
              <TradeLegMakerSlider
                ref={sliderRef as React.Ref<HTMLDivElement>}
                symbol={symbol}
                color={color}
              />
              {makerTokens.length > 2 && (
                <LockButton
                  symbol={symbol}
                  canLock={canLock("maker", makerTokens)}
                  isLocked={selectedToken?.isLocked}
                  size={36}
                />
              )}
            </SliderContainer>
            <AmountAndUsdPriceFullWidth
              symbol={symbol}
              amount={amount}
              usdValue={price.mul(amount).value}
              isAmountShown={true}
              dimAmountValues={isSliderMoving && !isRequestingQuote}
            />
          </>
        )}
      </TradeRowWrapper>
    </Container>
  );
};

// Sub components

const Container = styled.div(() => {
  return { padding: "0px 10px", width: "100%" };
});

const SliderContainer = styled.div(() => {
  return {
    position: "relative",
    top: -22,
    display: "flex",
    flexDirection: "column",
    gap: 6,
    alignItems: "center",
  };
});

const TradeRowWrapper = styled(AnimateChangeInHeight)<{
  showSlider: boolean;
  counterParty: CounterParty;
}>`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 4px;
  border-radius: 24px;
  padding: 14px 16px;
  overflow: hidden;
  background: ${props => (props.showSlider ? props.theme.surface.main.tertiary.default : "none")};
`;

const TokenInputAndBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  align-self: stretch;
`;

const MainSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  align-self: stretch;
`;

const SubSection = styled.div(() => {
  return { display: "flex", width: "100%", justifyContent: "space-between" };
});

const PercentageDisplay = ({ pct, color }: { pct: number; color: string }) => {
  return (
    <Text textStyle="Title" size="XL" color={color}>
      {pct}%
    </Text>
  );
};
